/**
 * The third-party icon sets keepr draws from (KPR-1185, KPR-1189): one entry
 * per react-icons module the app imports (`module`) and one per pinned
 * Iconify set the registry names (`iconify`, its prefix). It is the data
 * behind the guide at docs/guides/getting-started/open-source-licences.md,
 * whose Icon sets table and credits are written from it
 * (`renderIconSetsSection`, by `npm run build:icons`), and the picker's
 * credit line. src/config/__tests__/iconLicences.test.js fails when an icon's
 * set is not listed here, when a listed licence is not one keepr accepts, or
 * when the guide is not what this file writes.
 *
 * `spdx` is the licence's SPDX id; only ACCEPTED_SPDX may ship. A set whose
 * licence asks for attribution (CC BY) is marked `attribution` and credited
 * in full in the guide, with `credit` saying how keepr adapts it.
 *
 * PURE: no imports. Callers pass the react-icons modules in (`iconSetOf`).
 */

/** The licences an icon set may carry: permissive, public domain, or attribution only. */
export const ACCEPTED_SPDX = Object.freeze(['MIT', 'ISC', 'Apache-2.0', 'CC0-1.0', 'CC-BY-3.0', 'CC-BY-4.0']);

/** True for a licence an icon set may ship under. Share-alike, GPL, OFL and the rest are refused. */
export function isAcceptedLicence(spdx) {
	return ACCEPTED_SPDX.includes(spdx);
}

/** True for a licence that asks for the author to be credited. */
export function needsAttribution(spdx) {
	return /^CC-BY-/.test(spdx);
}

export const ICON_LICENCES_GUIDE_PATH = '/docs/guides/getting-started/open-source-licences';

export const ICON_LICENCES = [
	{ module: 'ai', spdx: 'MIT', name: 'Ant Design Icons', authors: 'Ant Design contributors', licence: 'MIT', licenceUrl: 'https://opensource.org/licenses/MIT', url: 'https://github.com/ant-design/ant-design-icons' },
	{ module: 'bs', spdx: 'MIT', name: 'Bootstrap Icons', authors: 'The Bootstrap authors', licence: 'MIT', licenceUrl: 'https://github.com/twbs/icons/blob/main/LICENSE', url: 'https://icons.getbootstrap.com' },
	{ module: 'gi', spdx: 'CC-BY-3.0', name: 'Game Icons', authors: 'Lorc, Delapouite and contributors', licence: 'CC BY 3.0', licenceUrl: 'https://creativecommons.org/licenses/by/3.0/', url: 'https://game-icons.net', attribution: true, credit: 'keepr adapts them: each one is resized, and drawn in a single color on the tile of the record\'s color.' },
	{ iconify: 'game-icons', spdx: 'CC-BY-3.0', name: 'Game Icons (newer drawings)', authors: 'Lorc, Delapouite and contributors', licence: 'CC BY 3.0', licenceUrl: 'https://creativecommons.org/licenses/by/3.0/', url: 'https://game-icons.net', attribution: true, credit: 'keepr adapts them: each one is resized, and drawn in a single color on the tile of the record\'s color.' },
	{ iconify: 'iconoven', spdx: 'MIT', name: 'IconOven', authors: 'Appic Marketing', licence: 'MIT', licenceUrl: 'https://opensource.org/licenses/MIT', url: 'https://iconoven.com/icons' },
	{ module: 'io5', spdx: 'MIT', name: 'Ionicons', authors: 'Ionic', licence: 'MIT', licenceUrl: 'https://github.com/ionic-team/ionicons/blob/main/LICENSE', url: 'https://ionic.io/ionicons' },
	{ module: 'lia', spdx: 'MIT', name: 'Line Awesome', authors: 'Icons8', licence: 'MIT', licenceUrl: 'https://github.com/icons8/line-awesome/blob/master/LICENSE.md', url: 'https://icons8.com/line-awesome' },
	{ module: 'lu', spdx: 'ISC', name: 'Lucide', authors: 'Lucide contributors', licence: 'ISC', licenceUrl: 'https://github.com/lucide-icons/lucide/blob/main/LICENSE', url: 'https://lucide.dev' },
	{ iconify: 'mdi', spdx: 'Apache-2.0', name: 'Material Design Icons (community)', authors: 'Pictogrammers and contributors', licence: 'Apache 2.0', licenceUrl: 'https://github.com/Templarian/MaterialDesign/blob/master/LICENSE', url: 'https://pictogrammers.com/library/mdi/' },
	{ module: 'md', spdx: 'Apache-2.0', name: 'Material Design icons', authors: 'Google', licence: 'Apache 2.0', licenceUrl: 'https://github.com/google/material-design-icons/blob/master/LICENSE', url: 'https://fonts.google.com/icons' },
	{ iconify: 'pinhead', spdx: 'CC0-1.0', name: 'Pinhead', authors: 'Quincy Morgan', licence: 'CC0 1.0', licenceUrl: 'https://creativecommons.org/publicdomain/zero/1.0/', url: 'https://github.com/waysidemapping/pinhead' },
	{ module: 'pi', spdx: 'MIT', name: 'Phosphor Icons', authors: 'Helena Zhang and Tobias Fried', licence: 'MIT', licenceUrl: 'https://github.com/phosphor-icons/core/blob/main/LICENSE', url: 'https://phosphoricons.com' },
	{ module: 'ri', spdx: 'Apache-2.0', name: 'Remix Icon', authors: 'Remix Design', licence: 'Apache 2.0', licenceUrl: 'https://github.com/Remix-Design/RemixIcon/blob/master/License', url: 'https://remixicon.com' },
	{ module: 'rx', spdx: 'MIT', name: 'Radix Icons', authors: 'WorkOS', licence: 'MIT', licenceUrl: 'https://github.com/radix-ui/icons/blob/master/LICENSE', url: 'https://icons.radix-ui.com' },
	{ module: 'si', spdx: 'CC0-1.0', name: 'Simple Icons', authors: 'Simple Icons collaborators', licence: 'CC0 1.0', licenceUrl: 'https://creativecommons.org/publicdomain/zero/1.0/', url: 'https://simpleicons.org' },
	{ module: 'tb', spdx: 'MIT', name: 'Tabler Icons', authors: 'Pawel Kuna and contributors', licence: 'MIT', licenceUrl: 'https://github.com/tabler/tabler-icons/blob/main/LICENSE', url: 'https://tabler.io/icons' },
	{ module: 'tfi', spdx: 'MIT', name: 'Themify Icons', authors: 'Themify', licence: 'MIT', licenceUrl: 'https://github.com/thecreation/standard-icons/blob/master/modules/themify-icons/LICENSE', url: 'https://themify.me/themify-icons' },
];

/** The key a set is known by: `react-icons/<module>` or `iconify:<prefix>`. */
export function iconSetKey(set) {
	return set.iconify ? `iconify:${set.iconify}` : `react-icons/${set.module}`;
}

/**
 * The set a registry entry comes from, as `iconSetKey` spells it, or null
 * when no listed set holds it. A react-icons component is found by identity
 * among the exports of the modules passed in (`{ tb: require('react-icons/tb'), ... }`);
 * an Iconify reference by its prefix.
 */
export function iconSetOf(entry, reactModules, sets = ICON_LICENCES) {
	if (typeof entry === 'string') {
		const m = /^([a-z0-9-]+):/.exec(entry);
		const set = m && sets.find(s => s.iconify === m[1]);
		return set ? iconSetKey(set) : null;
	}
	if (typeof entry !== 'function') return null;
	for (const set of sets) {
		if (!set.module) continue;
		const exports = reactModules && reactModules[set.module];
		if (exports && Object.values(exports).includes(entry)) return iconSetKey(set);
	}
	return null;
}

/**
 * The generated part of the licences guide runs from its Icon sets heading to
 * the heading after it (Brand marks), which stays hand-written. Guides strip
 * raw HTML, so the headings are the markers.
 */
export const GUIDE_SECTION_START = '## Icon sets';
export const GUIDE_SECTION_END = '## Brand marks';

/**
 * The licences guide's generated part: the Icon sets table (every set,
 * sorted by name) and, for each set whose licence asks for it, its credit.
 * Ends with a blank line, before the Brand marks heading.
 */
export function renderIconSetsSection(sets = ICON_LICENCES) {
	const sorted = [...sets].sort((a, b) => a.name.localeCompare(b.name, 'en'));
	const lines = [
		GUIDE_SECTION_START,
		'',
		'| Set | Authors | Licence |',
		'|---|---|---|',
		...sorted.map(set => `| [${set.name}](${set.url}) | ${set.authors} | [${set.licence}](${set.licenceUrl}) |`),
	];
	const credited = sorted.filter(set => set.attribution);
	if (credited.length) {
		lines.push('', '## Credit required by the licence');
		for (const set of credited) {
			const host = set.url.replace(/^https?:\/\//, '').replace(/\/$/, '');
			lines.push('', `${set.name} are by ${set.authors}, from [${host}](${set.url}), and are licensed under [${set.licence}](${set.licenceUrl}).${set.credit ? ` ${set.credit}` : ''}`);
		}
	}
	return `${lines.join('\n')}\n\n`;
}

/** The guide's text with its generated part replaced by `renderIconSetsSection(sets)`. */
export function writeIconSetsSection(guide, sets = ICON_LICENCES) {
	const start = guide.indexOf(`\n${GUIDE_SECTION_START}\n`);
	const end = guide.indexOf(`\n${GUIDE_SECTION_END}\n`);
	if (start === -1 || end === -1 || end < start) throw new Error(`the licences guide has no "${GUIDE_SECTION_START}" heading followed by "${GUIDE_SECTION_END}"`);
	return guide.slice(0, start + 1) + renderIconSetsSection(sets) + guide.slice(end + 1);
}
